import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { message } from 'antd';
import queryString from 'query-string';

import { REFRESH_URL } from '@/shared/api/constants';

let showErrorNotification: ((message: string, description?: string) => void) | null =
  null;

export const setNotificationHandler = (
  handler: (message: string, description?: string) => void,
) => {
  showErrorNotification = handler;
};

export const baseQuery = fetchBaseQuery({
  baseUrl: '/',
  paramsSerializer: (params) => queryString.stringify(params),
  credentials: 'include',
});

let refreshPromise: Promise<boolean> | null = null;

/**
 * Обновляет сессию через keycloakProvider: GET REFRESH_URL с cookie JSESSIONID.
 * 200 — токены в сессии обновлены, 401 — refresh token истёк (сессия завершена).
 * Параллельные запросы, получившие 401, ждут одно общее обновление.
 */
const refreshSession = (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = fetch(REFRESH_URL, { method: 'GET', credentials: 'include' })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const getRequestUrl = (args: string | FetchArgs) =>
  typeof args === 'string' ? args : args.url;

/**
 * При 401 один раз обновляет сессию и повторяет запрос. Если обновить не удалось,
 * возвращается исходный ответ 401 (дальше срабатывает редирект на LOGIN_URL в getMe).
 */
const baseQueryWithSessionRefresh: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extra) => {
  const result = await baseQuery(args, api, extra);

  if (result.error?.status !== 401 || getRequestUrl(args) === REFRESH_URL) {
    return result;
  }

  const isRefreshed = await refreshSession();

  return isRefreshed ? baseQuery(args, api, extra) : result;
};

export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extra) => {
  try {
    const result = await baseQueryWithSessionRefresh(args, api, extra);

    if (result.error) {
      const errorData = result.error.data as any;
      const errorMessage = errorData?.message || errorData?.error || 'Произошла ошибка';

      if (showErrorNotification) {
        showErrorNotification('Ошибка запроса', errorMessage);
      } else {
        message.error(errorMessage);
      }

      console.error('API Error:', result.error);
    }

    return result;
  } catch (error) {
    console.error('Unhandled error:', error);
    if (showErrorNotification) {
      showErrorNotification('Ошибка', 'Произошла непредвиденная ошибка');
    }
    throw error;
  }
};
